/* eslint-disable @stencil-community/required-jsdoc */
import { Component, h, Prop, Host, Event, EventEmitter } from '@stencil/core';
import { isEmpty } from '@springbokagency/stencil-base';
import { ClickActionTarget, ClickActionType, type ClickActionEvent } from './click-action.types';

/**
 * The click action component.
 */
@Component({
  tag: 'efb-click-action',
  styleUrl: 'click-action.scss',
  shadow: false,
})
export class EfbClickAction {
  /**
   * The name for the EfbClickAction event.
   */
  @Prop() name?: string;

  /**
   * The value for the EfbClickAction event.
   */
  @Prop() value?: string | { [key: string]: string };

  /**
   * The type of button for the EfbClickAction component.
   */
  @Prop() type?: ClickActionType = ClickActionType.Button;

  /**
   * The href of EfbClickAction component, will convert the button into a link.
   */
  @Prop() href?: string;

  /**
   * The target of the provided href.
   */
  @Prop() target?: ClickActionTarget;

  /**
   * The class of the button or link.
   */
  @Prop() tagClass?: string;

  /**
   * Disabled state of inner tag.
   */
  @Prop() disabled?: boolean;

  /**
   * Use aria-label attribute when link/button doesn't have any text
   */
  @Prop() ariaLabelText?: string;

  /**
   * Data analytics attribute
   */
  @Prop() dataAnalytics?: string;

  /**
   * Events
   */
  @Event() efbClickAction: EventEmitter<ClickActionEvent>;

  /**
   * Methods
   */

  private handleClick(event: MouseEvent) {
    const { name, value, type, href, target, efbClickAction, dataAnalytics } = this;

    if (href && target !== ClickActionTarget.Internal) {
      return;
    }

    efbClickAction.emit({ name, value, type, href, target, dataAnalytics });
    event.stopPropagation();
    event.preventDefault();
  }

  render() {
    const { type, href, target, disabled } = this;
    const TagType = !isEmpty(href) ? 'a' : 'button';
    const attrs = TagType === 'button' ? { type, disabled } : { href, target, disabled };

    return (
      <Host class="efb-click-action">
        <TagType
          class={this.tagClass}
          {...attrs}
          aria-label={this.ariaLabelText}
          onClick={this.handleClick.bind(this)}
        >
          <slot />
        </TagType>
      </Host>
    );
  }
}
